import type { ReactElement } from "react";
import { cn } from "../../lib/utils.js";
import { TONES } from "../../lib/tones.js";

/* Split out of Textarea.tsx (textarea-autoresize-code) to keep that file
   under the 200-line soft cap. The row under a Textarea: the hint/error
   message and the count. A ghost field with none of the three renders
   nothing, so a borderless editor leaves no empty gap. */

export interface TextareaMetaRowProps {
  readonly ghost: boolean;
  readonly message?: string | undefined;
  readonly messageId?: string | undefined;
  readonly hasError: boolean;
  readonly hintLive?: boolean | undefined;
  readonly showCount?: boolean | undefined;
  readonly len: number;
  readonly countLimit?: number | undefined;
  readonly maxLength?: number | undefined;
}

export function TextareaMetaRow({
  ghost,
  message,
  messageId,
  hasError,
  hintLive,
  showCount,
  len,
  countLimit,
  maxLength,
}: TextareaMetaRowProps): ReactElement | null {
  const hasCountLimit = countLimit !== undefined;
  const overLimit = hasCountLimit && len > countLimit;
  if (ghost && !message && !showCount && !hasCountLimit) return null;
  return (
    <div className="flex justify-between items-center gap-3 mt-1">
      {message && (
        <p
          id={messageId}
          aria-live={!hasError && hintLive ? "polite" : undefined}
          className={cn(
            "text-dense-1",
            hasError ? "text-pb-red-text" : "text-pb-text-muted",
          )}
        >
          {message}
        </p>
      )}
      {showCount && (
        <p className="text-dense-1 text-pb-text-muted ms-auto shrink-0 whitespace-nowrap pb-tabular">
          {len}
          {maxLength ? ` / ${maxLength}` : ""}
        </p>
      )}
      {!showCount && hasCountLimit && (
        <p
          className={
            overLimit
              ? cn(
                  "text-dense-1 ms-auto shrink-0 whitespace-nowrap pb-tabular",
                  TONES.watch.text,
                )
              : "text-dense-1 text-pb-text-muted ms-auto shrink-0 whitespace-nowrap pb-tabular"
          }
        >
          {len} / {countLimit}
        </p>
      )}
    </div>
  );
}
